import { handleFormError, handleSubmit } from "app/client/lib/formUtils";
import { buildURL } from "app/client/lib/urlUtils";
import { textInput } from "app/client/ui/inputs";
import { LoginAPI } from "app/client/ui/LoginAPI";
import * as css from "app/client/ui/LoginPagesCss";
import { buildValidatedPasswordInput, InvalidPasswordError } from "app/client/ui/ValidatedPasswordInput";
import { textButton } from "app/client/ui2018/buttons";
import { cssLink } from "app/client/ui2018/links";
import { loadingSpinner } from "app/client/ui2018/loaders";
import { ApiError } from "app/common/ApiError";
import { ResetPasswordResponse } from "app/common/LoginAPI";

import { dom, IDisposableOwner, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-fpp-");

interface ResetFormOptions {
  loginAPI: LoginAPI;
  email: Observable<string>;
  onSuccess: (resp: ResetPasswordResponse) => void;
  onEmailVerificationRequired: () => void;
}

/**
 * Builds a password reset form for a Grist account.
 *
 * Prompts for an email address to send a verification code to.
 *
 * On successful submission, `onSuccess` will be called, which advances the flow
 * to confirmation (see `buildConfirmResetPasswordForm`).
 *
 * If the user associated with the email is unverified, `onEmailVerificationRequired`
 * will be called, which changes the flow to email verification.
 */
export function buildResetPasswordForm(owner: IDisposableOwner, options: ResetFormOptions) {
  const { loginAPI, email: emailObs, onSuccess, onEmailVerificationRequired } = options;

  const error = Observable.create(owner, "");
  const pending = Observable.create(owner, false);

  return dom.frag(
    css.formHeading("Forgot your password?", testId("heading")),
    dom("form",
      css.formInstructions(
        css.text("Enter your email and we will send a message to reset your password."),
        testId("form-instructions"),
      ),
      dom.maybe(error, () => css.formError(dom.text(error), testId("form-error"))),
      cssEmail(
        css.formLabel("Email", { for: "email" }),
        textInput(emailObs,
          {
            id: "email",
            name: "email",
            autocomplete: "username",
            placeholder: "name@host.com",
            type: "email",
            required: "true",
          },
          // TODO: Investigate why a timeout of 0 sometimes doesn't work.
          (el) => { setTimeout(() => el.focus(), 10); },
          testId("email-input"),
        ),
      ),
      css.bigPrimaryButton("Reset password",
        dom.boolAttr("disabled", pending),
        testId("reset-password"),
      ),
      cssBackTextButton(cssLink("← Back to sign in",
        { href: buildURL("/login").href },
        testId("sign-in"),
      )),
      handleSubmit({
        pending,
        onSubmit: ({ email }) => loginAPI.resetPassword(email),
        onSuccess: resp => onSuccess(resp),
        onError: (e) => {
          if (e instanceof ApiError && e.details?.code === "UserNotConfirmed") {
            onEmailVerificationRequired();
          }
          else {
            handleFormError(e, error);
          }
        },
      }),
    ),
  );
}

interface ConfirmResetFormOptions {
  loginAPI: LoginAPI;
  email: Observable<string>;
  username: string;
}

/**
 * Builds a post-registration form that prompts for a verification code sent via email.
 */
export function buildConfirmResetPasswordForm(owner: IDisposableOwner, options: ConfirmResetFormOptions) {
  const { loginAPI, email } = options;

  const verificationCode = Observable.create(owner, "");
  const isResendingCode = Observable.create(owner, false);
  const resentCode = Observable.create(owner, false);
  const passwordObs = Observable.create(owner, "");
  const isPasswordValid = Observable.create(owner, false);
  const error = Observable.create(owner, "");
  const pending = Observable.create(owner, false);

  owner.autoDispose(resentCode.addListener((didResend) => {
    if (!didResend) { return; }

    error.set("");
    verificationCode.set("");
    passwordObs.set("");
  }));

  let { username } = options;
  let passwordInput: HTMLElement;

  return dom.frag(
    dom.domComputed(isResendingCode, (isLoading) => {
      if (isLoading) { return css.spinner(loadingSpinner()); }

      return [
        css.formHeading(dom.text(use => use(resentCode) ? "Sent again!" : "Change password"), testId("heading")),
        dom("form",
          css.formInstructions(
            css.text("We have sent a verification code by email to ", css.lightlyBolded(email.get()), "."),
            css.text("Enter it below to reset your password."),
            testId("form-instructions"),
          ),
          dom.maybe(error, () => css.formError(dom.text(error), testId("form-error"))),
          cssVerificationCode(
            css.formLabel("Verification Code", { for: "code" }),
            css.codeInput(verificationCode,
              {
                id: "code",
                name: "code",
                type: "text",
                autocomplete: "off",
                inputmode: "numeric",
                pattern: "\\d{6}",
                maxlength: "6",
                required: "true",
              },
              (el) => { setTimeout(() => el.focus(), 10); },
              testId("code-input"),
            ),
            css.resendCode("Didn't receive a code? ",
              css.textButton(
                "Resend it",
                textButton.cls("-hover-bg-padding-sm"),
                { type: "button" },
                dom.on("click", async () => {
                  if (pending.get()) { return; }

                  try {
                    isResendingCode.set(true);
                    const resp = await loginAPI.resetPassword(email.get());
                    username = resp.username;
                    resentCode.set(true);
                  }
                  catch (e) {
                    handleFormError(e, error);
                  }
                  finally {
                    isResendingCode.set(false);
                  }
                }),
                dom.boolAttr("disabled", pending),
                testId("resend-code"),
              ),
            ),
          ),
          cssNewPassword(
            css.formLabel("New password", { for: "new-password" }),
            dom.create(buildValidatedPasswordInput, passwordObs, isPasswordValid,
              (el) => { passwordInput = el; },
              testId("password-input"),
            ),
          ),
          css.bigPrimaryButton("Change password",
            dom.boolAttr("disabled", pending),
            testId("confirm"),
          ),
          cssBackTextButton(
            cssLink("← Back to sign in",
              { href: buildURL("/login").href },
              testId("sign-in"),
            ),
          ),
          handleSubmit({
            pending,
            onSubmit: async ({ code, password }) => {
              if (!isPasswordValid.get()) { throw new InvalidPasswordError(); }

              await loginAPI.confirmResetPassword(username, password, code);
            },
            onSuccess: () => window.location.assign(buildURL("/login")),
            onError: (e) => {
              if (e instanceof InvalidPasswordError) {
                return passwordInput.focus();
              }

              handleFormError(e, error);
            },
          }),
        ),
      ];
    }),
  );
}

const cssVerificationCode = styled("div", `
  margin-bottom: 20px;
`);

const cssEmail = styled("div", `
  margin-bottom: 32px;
`);

const cssNewPassword = styled("div", `
  margin-bottom: 32px;
`);

const cssBackTextButton = styled(css.text, `
  margin-top: 24px;
`);
